import React from 'react';

const Sidebar = ({
  clearChat,
  agentMode,
  setAgentMode,
  webSearchEnabled,
  setWebSearchEnabled,
  ragEnabled,
  setRagEnabled,
  memoryEnabled,
  setMemoryEnabled,
  isDarkTheme,
  toggleTheme,
  memoryStats,
  customPrompt,
  promptInputValue,
  setPromptInputValue,
  showPromptEditor,
  setShowPromptEditor,
  updateCustomPrompt,
  resetToDefaultPrompt
}) => {
  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <button onClick={clearChat} className="new-chat-btn">
          + New chat
        </button>
      </div>

      <div className="sidebar-content">
        {/* Tools section */}
        <div className="tools-section">
          <h3>Tools</h3>

          {/* Agent Mode Toggle */}
          <div className="tool-option" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '8px' }}>
            <label className="toggle">
              <input
                type="checkbox"
                checked={agentMode}
                onChange={(e) => {
                  setAgentMode(e.target.checked);
                }}
              />
              🤖 Agent Mode
            </label>
            {agentMode && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                AI automatically selects tools
              </div>
            )}
          </div>

          {/* Manual toggles - only show when agent mode is off */}
          {!agentMode && (
            <>
              <div className="tool-option">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={webSearchEnabled}
                    onChange={(e) => setWebSearchEnabled(e.target.checked)}
                  />
                  🌐 Search the web
                </label>
              </div>
              <div className="tool-option">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={ragEnabled}
                    onChange={(e) => setRagEnabled(e.target.checked)}
                  />
                  📚 Knowledge base
                </label>
              </div>
            </>
          )}

          {/* Memory toggle - always visible */}
          <div className="tool-option">
            <label className="toggle">
              <input
                type="checkbox"
                checked={memoryEnabled}
                onChange={(e) => setMemoryEnabled(e.target.checked)}
              />
              🧠 Adaptive Memory
            </label>
          </div>
        </div>

        {/* Custom Prompt Section */}
        <div className="tools-section" style={{ marginTop: '12px' }}>
          <h3>🎯 Custom Prompt</h3>

          <div className="tool-option">
            <button
              onClick={() => setShowPromptEditor(!showPromptEditor)}
              className="prompt-toggle-btn"
              style={{
                width: '100%',
                padding: '6px 8px',
                backgroundColor: 'var(--hover-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                color: 'var(--text-primary)',
                fontSize: '11px',
                cursor: 'pointer',
                marginBottom: '6px'
              }}
            >
              {showPromptEditor ? '🔼 Hide' : '🔽 Edit'}
            </button>

            {showPromptEditor && (
              <div style={{ marginTop: '6px' }}>
                <textarea
                  value={promptInputValue}
                  onChange={(e) => setPromptInputValue(e.target.value)}
                  placeholder="Enter your custom system prompt..."
                  className="prompt-editor-textarea"
                />

                <div className="prompt-editor-buttons">
                  <button
                    onClick={updateCustomPrompt}
                    className="prompt-editor-btn update"
                  >
                    ✅ Save
                  </button>

                  <button
                    onClick={resetToDefaultPrompt}
                    className="prompt-editor-btn reset"
                  >
                    🔄 Reset
                  </button>
                </div>

                <div className="prompt-current-preview">
                  <strong>Current:</strong> {customPrompt.length > 50 ? `${customPrompt.substring(0, 50)}...` : customPrompt}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Memory Statistics */}
        {memoryStats && (
          <div className="memory-section">
            <h3>🧠 Memory System</h3>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              <div>Interactions: {memoryStats.interactions}</div>
              <div>Corrections: {memoryStats.corrections}</div>
              <div>Total Memories: {memoryStats.total_nodes}</div>
            </div>

          </div>
        )}

        {/* System Tools Section */}
        <div className="tools-section" style={{ marginTop: '12px' }}>
          <h3>⚙️ System Tools</h3>

          <div className="tool-option">
            <button
              id="dropbox-process-btn"
              onClick={async (e) => {
                const btn = e.currentTarget;
                const originalText = btn.textContent;

                // Disable button and show loading
                btn.disabled = true;
                btn.textContent = '⏳ Processing... (this may take few minutes)';
                btn.style.backgroundColor = '#888';
                btn.style.cursor = 'wait';

                try {
                  console.log('🔄 Starting Dropbox processing...');
                  const startTime = Date.now();

                  const response = await fetch('/api/run_unified_dropbox', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                  });
                  const result = await response.json();

                  const elapsed = Math.round((Date.now() - startTime) / 1000);

                  if (result.success) {
                    console.log('✅ Dropbox processing completed successfully');
                    alert(`✅ Dropbox processing completed!\n\nTime: ${elapsed} seconds\nTimestamp: ${result.timestamp || 'N/A'}`);
                  } else {
                    console.error('❌ Dropbox processing failed:', result.error);
                    alert(`❌ Processing failed:\n${result.error}`);
                  }
                } catch (error) {
                  console.error('❌ Dropbox processing error:', error);
                  alert(`❌ Error: ${error.message}\n\nCheck console for details.`);
                } finally {
                  // Re-enable button
                  btn.disabled = false;
                  btn.textContent = originalText;
                  btn.style.backgroundColor = 'var(--accent-color)';
                  btn.style.cursor = 'pointer';
                }
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: 'var(--accent-color)',
                border: '1px solid var(--accent-color)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              🔄 Run Dropbox Processing
            </button>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Downloads &amp; indexes legal documents from Dropbox. May take 10-15 min.
            </div>
          </div>
        </div>

        <label className="toggle">
          <input
            type="checkbox"
            checked={isDarkTheme}
            onChange={toggleTheme}
          />
          🌙 Dark Theme
        </label>
      </div>
    </div>
  );
};

export default Sidebar;
